/**
 * Single source of truth for all article metadata
 * 
 * This file centralizes all article information to prevent duplication
 * and inconsistencies between src/articles and public/articles.
 * 
 * The actual markdown content and images remain in public/articles/
 * for runtime fetching and web serving.
 */

export const ARTICLES = [
  {
    id: "kubernetes-health-probes-guide",
    title: "Kubernetes: Health Probes",
    description: "How to maximize production stability using livenessProbe, readinessProbe, and startupProbe",
    author: "Amit Halfon",
    date: "2025-03-20",
    readTime: "10 min read",
    coverImage: "/articles/images/probes.png",
    tags: ["Kubernetes", "Cloud", "Production"],
    featured: true,
    published: true,
    filename: "kubernetes-health-probes-guide.md"
  },
  {
    id: "mastering-horizontal-pod-autoscaler",
    title: "kubernetes: Horizontal Pod Autoscaler (HPA)",
    description: "How to define HPA for automatic pod scaling based on CPU, memory, and custom metrics",
    author: "Amit Halfon",
    date: "2025-05-15",
    readTime: "8 min read",
    coverImage: "/articles/images/hpa-horizontalpodautoscaler.png",
    tags: ["Kubernetes", "Scale"],
    featured: true,
    published: true,
    filename: "mastering-horizontal-pod-autoscaler.md"
  },
  {
    id: "sharding-vs-partitioning",
    title: "Sharding vs Partitioning",
    description: "When data volume explodes, your database architecture must evolve. Is the solution to distribute data across multiple servers (Sharding) or to optimize table organization within a single instance (Partitioning)?",
    author: "Amit Halfon",
    date: "2026-01-09",
    readTime: "6 min read",
    coverImage: "/articles/images/sharding-vs-partitioning.png",
    tags: ["Scale", "Database"],
    featured: true,
    published: true,
    filename: "sharding-vs-partitioning.md" 
  }
  ,
  {
    id: "replication",
    title: "Replication with Sharding",
    description: "Learn the core principles of Database Replication and explore how combining it with Sharding allows you to scale effortlessly while eliminating single points of failure.",
    author: "Amit Halfon",
    date: "2025-12-08",
    readTime: "2 min read",
    coverImage: "/articles/images/replications-sharding.png",
    tags: ["Scale", "Database"],
    featured: true,
    published: true,
    filename: "replications.md" 
  }
  ,
  {
    id: "concurrency-vs-parallelism",
    title: "Concurrency vs Parallelism",
    description: "Stop using 'concurrency' and 'parallelism' interchangeably. Most developers think they mean the same thing and that mistake is costing them performance, scalability, and system stability.",
    author: "Amit Halfon",
    date: "2025-09-09",
    readTime: "7 min read",
    coverImage: "/articles/images/concurrency-vs-parallelism.png",
    tags: ["Concurrency", "Parallelism", "Performance"],
    featured: true,
    published: true,
    filename: "concurrency-vs-parallelism.md"
  },
  {
    id: "Resilient-1",
    title: "Rate Limiting & Circuit Breakers",
    description: "Resilient Systems Part 1: Stop your services from collapsing: Discover how Rate Limiters and Circuit Breakers can bulletproof your architecture against traffic spikes and cascading failures.",
    author: "Amit Halfon",
    date: "2026-03-23",
    readTime: "5 min read",
    coverImage: "/articles/images/Resilient1.png",
    tags: ["Distributed Systems", "Resilience", "Architecture"],
    featured: true,
    published: true,
    filename: "Resilient1.md"
  },
  {
    id: "Resilient-2",
    title: "Retry Mechanisms & Connection Pooling",
    description: "Resilient Systems Part 2: Learn how exponential backoff with jitter and connection pooling help distributed systems recover from transient failures and scale efficiently under load.",
    author: "Amit Halfon",
    date: "2026-04-18",
    readTime: "6 min read",
    coverImage: "/articles/images/Resilient2.png",
    tags: ["Distributed Systems", "Resilience", "Architecture"],
    featured: true,
    published: true,
    filename: "Resilient2.md"
  }
  ,
  {
    id: "memory-management",
    title: "Memory Management : Stack, Heap, and Garbage Collection",
    description: "Unlock the secrets of high-performance code by mastering the invisible interplay between Stack and Heap memory and the mechanics that keep them running.",
    author: "Amit Halfon",
    date: "2025-06-23",
    readTime: "3 min read",
    coverImage: "/articles/images/memory-management.png",
    tags: ["Stack Memory", "Heap Memory", "Dynamic Allocation", "Garbage Collection"],
    featured: true,
    published: true,
    filename: "memory-management.md"
  }
  /*{
    id: "kafka-partitions-scaling",
    title: "Scaling Kafka: Why more partitions is not a magic solution",
    description: "what is over partitioning in kafka? and when horizontal scaling stops being the answer?",
    author: "Amit Halfon",
    date: "2026-01-18",
    readTime: "7 min read",
    coverImage: "/articles/images/kafka.png",
    tags: ["Kafka", "Distributed Systems", "Performance"],
    featured: true,
    published: true,
    filename: "kafka-partitions-scaling.md"
  }, */
  // Add new articles here
];

/**
 * Get all published articles sorted by date (newest first)
 * @returns {Array} Array of published articles
 */
export const getPublishedArticles = () => 
  ARTICLES.filter(article => article.published)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

/**
 * Find a specific article by ID
 * @param {string} id - The article ID
 * @returns {Object|undefined} The article object or undefined if not found
 */
export const findArticleById = (id) => 
  ARTICLES.find(article => article.id === id && article.published);

/**
 * Get all featured articles
 * @returns {Array} Array of featured articles
 */
export const getFeaturedArticles = () =>
  ARTICLES.filter(article => article.published && article.featured)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

/**
 * Get articles by tag
 * @param {string} tag - The tag to filter by
 * @returns {Array} Array of articles with the specified tag
 */
export const getArticlesByTag = (tag) =>
  ARTICLES.filter(article => 
    article.published && article.tags.includes(tag)
  ).sort((a, b) => new Date(b.date) - new Date(a.date));

/**
 * Get all unique tags from published articles
 * @returns {Array} Array of unique tags
 */
export const getAllTags = () => {
  const tags = new Set();
  ARTICLES.forEach(article => {
    if (article.published) {
      article.tags.forEach(tag => tags.add(tag));
    }
  });
  return Array.from(tags).sort();
};